import { createUserWithEmailAndPassword } from "firebase/auth"
import { doc, setDoc, Timestamp } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import { Eye, EyeOff } from "lucide-react"
import { FC, useState } from "react"
import { useAuth, useFirestore, useFunctions } from "../hooks/use-firebase"
import { Button, Input, Checkbox } from "../components/ui"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import TermsOfServiceDialog from "../components/TermsOfServiceDialog"
import PrivacyPolicyDialog from "../components/PrivacyPolicyDialog"

export const CreateAccount: FC = ({ }) => {
  const auth = useAuth()
  const db = useFirestore()
  const functions = useFunctions()
  const navigate = useNavigate()

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [visible, setVisible] = useState(false)
  const [visibleConfirm, setVisibleConfirm] = useState(false)
  const [acceptedPolicies, setAcceptedPolicies] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const validateForm = () => {
    if (!firstName.trim()) {
      setError("First name is required")
      return false
    }
    if (!lastName.trim()) {
      setError("Last name is required")
      return false
    }
    if (!email.trim()) {
      setError("Email is required")
      return false
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters")
      return false
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return false
    }
    if (!acceptedPolicies) {
      setError("You must accept the Terms of Service and Privacy Policy")
      return false
    }
    return true
  }

  const handleCreateAccount = async () => {
    setError("")

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      // Create Firestore user document
      await setDoc(doc(db, "users", user.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        createAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        lastLogin: Timestamp.now(),
        acceptedPoliciesAt: Timestamp.now(),
        acceptedPolicies: true,
      })

      // Send welcome email with verification link via Resend
      try {
        const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail')
        await sendWelcomeEmail({ email: email.trim(), firstName: firstName.trim() })
        toast.success("Account created! Please check your email to verify your address.")
      } catch (verificationError: any) {
        console.error('Failed to send welcome email:', verificationError)
        toast.success("Account created successfully!")
        toast.error("Failed to send verification email. You can request a new one later.")
      }

      // Navigate to home - the subscription dialog will show automatically
      navigate("/")
    } catch (e: any) {
      console.error('Account creation failed:', e)

      if (e.code) {
        switch (e.code) {
          case 'auth/email-already-in-use':
            setError('An account with this email already exists.')
            break
          case 'auth/invalid-email':
            setError('The email address is not valid.')
            break
          case 'auth/operation-not-allowed':
            setError('Email/password accounts are not enabled. Please contact support.')
            break
          case 'auth/weak-password':
            setError('The password is too weak. Please use a stronger password.')
            break
          default:
            setError('An unexpected error occurred. Please try again later.')
        }
      } else {
        setError(e.message || 'Unknown error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleCreateAccount()
    }
  }

  return (
    <div className="w-screen h-screen flex flex-col md:flex-row">
      <div className="w-none md:w-1/2 flex justify-center items-center">
        <div
          className="font-extrabold text-[30px] text-center text-black dark:text-white md:text-[60px] cursor-pointer"
          onClick={() => navigate("/")}
        >
          INVENTORY<span className="text-sumoPrimary">SUMO</span>
        </div>
      </div>

      <div className="w-full h-full md:w-1/2 bg-black flex justify-center items-center md:items-center">
        <div className="w-[392px] flex flex-col gap-4 p-6 bg-black text-white rounded-sm shadow">
          <div className="text-white text-2xl">Create Account</div>

          <div className="w-full flex flex-col gap-2">
            <div className="flex gap-2">
              <Input
                name="firstName"
                className="bg-white text-black h-12 flex-1"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <Input
                name="lastName"
                className="bg-white text-black h-12 flex-1"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>

            <Input
              name="email"
              className="bg-white text-black h-12"
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />

            <div className="text-white flex items-center w-full">
              <Input
                name="password"
                className="bg-white text-black h-12"
                placeholder="Password"
                type={visible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value.trim())}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <div className="-ml-8 cursor-pointer" onClick={() => setVisible(!visible)}>
                {visible ? <Eye width={18} /> : <EyeOff width={18} />}
              </div>
            </div>

            <div className="text-white flex items-center w-full">
              <Input
                name="confirmPassword"
                className="bg-white text-black h-12"
                placeholder="Confirm Password"
                type={visibleConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value.trim())}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <div className="-ml-8 cursor-pointer" onClick={() => setVisibleConfirm(!visibleConfirm)}>
                {visibleConfirm ? <Eye width={18} /> : <EyeOff width={18} />}
              </div>
            </div>

            {error && (
              <div className="my-2 p-2 bg-error100 border border-2 border-error700 rounded text-error700">
                {error}
              </div>
            )}

            {/* Policy Acceptance Checkbox */}
            <div className="flex items-start gap-3 p-3 bg-neutral900 border border-neutral700 rounded-lg">
              <Checkbox
                id="accept-policies"
                checked={acceptedPolicies}
                onCheckedChange={(checked) => setAcceptedPolicies(checked as boolean)}
                className="mt-0.5"
                disabled={loading}
              />
              <label
                htmlFor="accept-policies"
                className="text-sm text-neutral300 leading-relaxed cursor-pointer"
              >
                I agree to the{' '}
                <span className="inline-block" onClick={(e) => e.stopPropagation()}>
                  <TermsOfServiceDialog
                    variant="link"
                    triggerClassName="text-sumoPrimary hover:text-sumoOrange underline underline-offset-2"
                    linkText="Terms of Service"
                  />
                </span>
                {' '}and{' '}
                <span className="inline-block" onClick={(e) => e.stopPropagation()}>
                  <PrivacyPolicyDialog
                    variant="link"
                    triggerClassName="text-sumoPrimary hover:text-sumoOrange underline underline-offset-2"
                    linkText="Privacy Policy"
                  />
                </span>
              </label>
            </div>
          </div>

          <div className="w-full flex flex-col gap-2">
            <Button
              variant="primary"
              className="w-full"
              disabled={!firstName || !lastName || !email || !password || !confirmPassword || !acceptedPolicies || loading}
              onClick={handleCreateAccount}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>

            <Button
              variant="ghost"
              className="w-full text-white hover:text-sumoPrimary"
              onClick={() => navigate("/login")}
              disabled={loading}
            >
              Already have an account? Log in
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

